<template>
  <div class="nup-board">
    <section class="nup-entry">
      <h3 class="nup-board__heading">
        <span class="nup-board__heading-text">{{ copy.entryTitle }}</span>
        <img
          class="nup-entry__badge"
          :class="isEn ? 'is-en' : ''"
          :src="badgeSrc"
          :alt="copy.guaranteed"
        >
      </h3>
      <div
        class="nup-entry__row"
        :class="showEntryCheck ? 'is-on' : ''"
      >
        <span
          v-if="!isPC || showEntryCheck"
          class="nup-adv__dot"
        />
        <span class="nup-entry__threshold">{{ copy.entryDesc }}</span>
      </div>
      <img
        v-if="pickedSrc"
        class="nup-entry__picked"
        :class="isEn ? 'is-en' : ''"
        :src="pickedSrc"
        alt=""
      >
      <img
        v-else
        class="nup-entry__combo"
        :src="entryComboSrc"
        alt=""
      >
      <div
        v-if="showChoiceBtn"
        class="nup-entry__pick-wrap"
      >
        <button
          type="button"
          class="nup-entry__pick"
          :style="{ backgroundImage: 'url(' + btnBg + ')' }"
          @click="$emit('pick', highlightKey)"
        >
          {{ copy.chooseThis }}
        </button>
      </div>
    </section>

    <div
      class="nup-board__plus"
      aria-hidden="true"
    >
      <img
        class="nup-board__plus-img"
        :src="plusImg"
        alt=""
      >
    </div>

    <section class="nup-adv">
      <h3 class="nup-board__heading">
        {{ copy.advancedTitle }}
      </h3>
      <p
        class="nup-board__desc"
        :class="[isPC ? 'is-pc' : '', isEn ? 'is-en' : '']"
      >
        <template v-if="isPC">
          <span
            v-for="(line, index) in descLines"
            :key="'desc-' + index"
          >{{ line }}</span>
        </template>
        <template v-else>{{ copy.advancedDesc }}</template>
      </p>
      <div class="nup-adv__track">
        <div
          v-for="(tier, index) in advancedTiers"
          :key="tier.id"
          class="nup-adv__item"
          :class="[
            itemClass(index),
            tier.kind === 'choice' ? 'is-choice' : '',
            tier.kind === 'choice' && showAdvancedChoiceBtn ? 'is-pickable' : '',
          ]"
        >
          <span
            v-if="tier.kind !== 'choice'"
            class="nup-adv__dot"
          />
          <!-- 8 万档：未选一张三选一整图，已选出对应 $10000 宽卡 -->
          <div
            v-if="tier.kind === 'choice'"
            class="nup-adv-choice"
          >
            <div class="nup-adv-choice__meta">
              <div class="nup-adv-choice__head">
                <span class="nup-adv__dot" />
                <span class="nup-adv-choice__threshold">{{ thresholdOf(tier) }}</span>
              </div>
              <button
                v-if="showAdvancedChoiceBtn"
                type="button"
                class="nup-adv-choice__pick"
                @click="$emit('pick-advanced', advancedHighlightKey)"
              >
                {{ copy.chooseReward }}
              </button>
            </div>
            <img
              class="nup-adv-choice__card"
              :class="advancedPickedSrc ? 'is-picked' : ''"
              :src="advancedPickedSrc || advancedComboSrc"
              alt=""
            >
          </div>
          <RewardTierRow
            v-else
            :tier="tier"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed } from 'vue-demi';
import { DEFAULT_ADVANCED_CHOICE_KEY, DEFAULT_CHOICE_KEY, isAdvancedChoiceKey, isChoiceKey, type AdvancedChoiceKey, type ChoiceKey } from '@ark/nup-shared';
import { getCopy } from '../constants/copy';
import { ADVANCED_TIERS, findAdvancedThreshold, findChoiceComboImage, findChoiceWideImage, findEntryBadge, type AdvancedTier } from '../constants/rewards';
import { advancedFilledCount, hasEntryAward, progressItemClass } from '../utils/progress-stage';
import { useNupLang } from '../composables/use-nup-lang';
import { usePageTypeFlag } from '../composables/use-page-type';
import btnBg from '../assets/rewards/btn-bg.png';
import plusImg from '../assets/rewards/plus.png';
import RewardTierRow from './reward-tier-row.vue';
const __sfc_main = {};
__sfc_main.props = {
  showChoiceBtn: {
    type: Boolean,
    default: false
  },
  showAdvancedChoiceBtn: {
    type: Boolean,
    default: false
  },
  choiceKey: {
    type: String,
    default: ''
  },
  advancedChoiceKey: {
    type: String,
    default: ''
  },
  enrolled: {
    type: Boolean,
    default: false
  },
  netDepositAmount: {
    type: Number,
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const advancedTiers = ADVANCED_TIERS;
  const {
    isPC
  } = usePageTypeFlag();
  const {
    isEn
  } = useNupLang();
  const copy = computed(() => getCopy(isEn.value));
  const descLines = computed(() => copy.value.advancedDesc.split('\n'));
  const badgeSrc = computed(() => findEntryBadge(isEn.value, isPC.value));
  const committedKey = computed<ChoiceKey | null>(() => {
    return isChoiceKey(props.choiceKey) ? props.choiceKey : null;
  });
  const committedAdvancedKey = computed<AdvancedChoiceKey | null>(() => {
    return isAdvancedChoiceKey(props.advancedChoiceKey) ? props.advancedChoiceKey : null;
  });
  const highlightKey = computed<ChoiceKey>(() => {
    return committedKey.value || DEFAULT_CHOICE_KEY;
  });
  const advancedHighlightKey = computed<AdvancedChoiceKey>(() => {
    return committedAdvancedKey.value || DEFAULT_ADVANCED_CHOICE_KEY;
  });
  const entryComboSrc = computed(() => {
    return findChoiceComboImage('entry', isEn.value, isPC.value);
  });
  const advancedComboSrc = computed(() => {
    return findChoiceComboImage('advanced', isEn.value, isPC.value);
  });
  const advancedPickedSrc = computed(() => {
    return committedAdvancedKey.value ? findChoiceWideImage(committedAdvancedKey.value, isEn.value, isPC.value, true) : '';
  });
  function thresholdOf(tier: AdvancedTier): string {
    return findAdvancedThreshold(tier, isEn.value);
  }
  const pickedSrc = computed(() => {
    return committedKey.value ? findChoiceWideImage(committedKey.value, isEn.value, isPC.value) : '';
  });
  const filledCount = computed(() => {
    return advancedFilledCount(props.enrolled, props.netDepositAmount);
  });
  const showEntryCheck = computed(() => {
    return hasEntryAward(props.enrolled, props.netDepositAmount);
  });
  function itemClass(index: number): string {
    return progressItemClass(index, showEntryCheck.value, filledCount.value);
  }
  return {
    btnBg,
    plusImg,
    advancedTiers,
    isPC,
    isEn,
    copy,
    descLines,
    badgeSrc,
    highlightKey,
    advancedHighlightKey,
    entryComboSrc,
    advancedComboSrc,
    advancedPickedSrc,
    thresholdOf,
    pickedSrc,
    showEntryCheck,
    itemClass
  };
};
__sfc_main.components = Object.assign({
  RewardTierRow
}, __sfc_main.components);
export default __sfc_main;
</script>

<style scoped>
.nup-board__heading {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  color: #000;
}

.nup-board__heading-text {
  flex-shrink: 0;
  white-space: nowrap;
}

.nup-board__desc {
  margin: 12px 0 28px;
  font-size: 26px;
  line-height: 1.4;
  color: rgba(48, 48, 48, 0.72);
}

.nup-entry__badge {
  display: block;
  width: 288px;
  height: 69px;
  margin-left: 12px;
  flex-shrink: 0;
}

.nup-entry__badge.is-en {
  width: 344px;
  height: 73px;
}

.nup-entry__row {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding-left: 48px;
  margin: 0 0 16px;
}

.nup-page.is-h5 .nup-entry__row.is-on {
  height: 20px;
  min-height: 20px;
}

.nup-page.is-h5.is-en .nup-entry__row.is-on {
  margin-top: 6px;
}

.nup-entry__row.is-on .nup-adv__dot {
  border-color: #ff6900;
  background: #ff6900;
}

.nup-entry__row.is-on .nup-adv__dot::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 0;
  width: 7px;
  height: 12px;
  border-right: 3px solid #fff;
  border-bottom: 3px solid #fff;
  transform: rotate(45deg);
}

.nup-entry__threshold {
  font-size: 30px;
  line-height: 1;
  color: #303030;
  letter-spacing: -0.4px;
}

.nup-entry__combo {
  display: block;
  width: 682px;
  height: auto;
  margin: 0 auto;
  flex-shrink: 0;
  object-fit: contain;
}

.nup-entry__picked {
  display: block;
  width: 670px;
  height: auto;
  margin: 0 auto;
  flex-shrink: 0;
  object-fit: contain;
}

.nup-entry__picked.is-en {
  width: 670px;
  height: 188px;
}

.nup-entry__pick-wrap {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

.nup-entry__pick {
  display: block;
  width: 614px;
  height: 106px;
  padding: 0;
  border: none;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 100%;
  color: #fff;
  font-size: 40px;
  font-weight: 600;
  line-height: 106px;
  text-align: center;
  cursor: pointer;
}

.nup-board__plus {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 40px 0;
}

.nup-board__plus::before,
.nup-board__plus::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e6e6e6;
}

.nup-board__plus-img {
  display: block;
  width: 61px;
  height: 63px;
  margin: 0 16px;
  flex-shrink: 0;
}

.nup-adv__track {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.nup-adv__item {
  position: relative;
  z-index: 1;
}

.nup-adv__item:not(:last-child)::before {
  content: '';
  position: absolute;
  z-index: 0;
  left: 13px;
  top: 50%;
  width: 4px;
  height: calc(100% + 20px);
  background: #e6e6e6;
}

.nup-adv__item.is-linked:not(:last-child)::before {
  background: #ff6900;
}

.nup-adv__dot {
  position: absolute;
  left: 0;
  top: 50%;
  z-index: 2;
  width: 30px;
  height: 30px;
  margin-top: -15px;
  border: 6px solid #d8d8d8;
  border-radius: 50%;
  background: #fff;
  box-sizing: border-box;
}

.nup-adv__item.is-active .nup-adv__dot {
  border-color: #ff6900;
  background: #fff;
}

.nup-adv__item.is-on .nup-adv__dot {
  border-color: #ff6900;
  background: #ff6900;
}

.nup-adv__item.is-on .nup-adv__dot::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 0;
  width: 7px;
  height: 12px;
  border-right: 3px solid #fff;
  border-bottom: 3px solid #fff;
  transform: rotate(45deg);
}

.nup-page.is-h5.is-en .nup-board__heading {
  color: #000;
  font-family: 'BR Hendrix', Arial, sans-serif;
  font-size: 42px;
  font-style: normal;
  font-weight: 600;
}

.nup-page.is-h5.is-en .nup-board__desc,
.nup-page.is-h5.is-en .nup-entry__threshold {
  color: #000;
  font-family: 'BR Hendrix', Arial, sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  opacity: 0.8;
  white-space: pre-line;
}

.nup-page.is-pc .nup-board__heading {
  justify-content: center;
  font-size: 42px;
}

.nup-page.is-pc .nup-board__desc,
.nup-board__desc.is-pc {
  display: flex;
  justify-content: center;
  width: 100%;
  margin: 8px auto 24px;
  font-size: 24px;
  color: #000;
  text-align: center;
}

.nup-page.is-pc .nup-board__desc span,
.nup-board__desc.is-pc span {
  flex: 0 0 auto;
  white-space: nowrap;
}

.nup-page.is-pc.is-en .nup-board__desc,
.nup-board__desc.is-pc.is-en {
  flex-direction: column;
  align-items: center;
}

.nup-page.is-pc .nup-entry__row {
  width: 1200px;
  max-width: 100%;
  min-height: 36px;
  padding-left: 0;
  margin: 12px auto 20px;
  justify-content: center;
  font-size: 24px;
}

.nup-page.is-pc .nup-entry__row .nup-adv__dot {
  position: relative;
  left: auto;
  top: auto;
  margin-top: 0;
  margin-right: 8px;
}

.nup-page.is-pc .nup-entry__row.is-on .nup-adv__dot::after {
  left: 4px;
  top: 0;
  width: 5px;
  height: 9px;
  border-right-width: 2px;
  border-bottom-width: 2px;
}

.nup-page.is-pc .nup-entry__threshold {
  font-size: 24px;
}

.nup-page.is-pc .nup-entry__badge {
  width: 286px;
  height: 68px;
  margin-left: 8px;
}

.nup-page.is-pc .nup-entry__badge.is-en {
  width: 344px;
  height: 73px;
}

.nup-page.is-pc .nup-entry__combo {
  width: 1185px;
}

.nup-page.is-pc .nup-entry__picked,
.nup-page.is-pc .nup-entry__picked.is-en {
  width: 591px;
  height: 152px;
}

.nup-page.is-pc .nup-entry__pick {
  width: 360px;
  height: 60px;
  font-size: 20px;
  line-height: 60px;
}

.nup-page.is-pc .nup-board__plus {
  width: 1200px;
  max-width: 100%;
  margin: 40px auto;
}

.nup-page.is-pc .nup-board__plus-img {
  width: 36px;
  height: 37px;
  margin: 0 12px;
}

.nup-page.is-pc .nup-adv__track {
  width: 1200px;
  max-width: 100%;
  margin: 0 auto;
  gap: 20px;
}

.nup-page.is-pc .nup-adv__item:not(:last-child)::before {
  left: 10px;
  width: 2px;
  height: calc(100% + 20px);
}

.nup-page.is-pc .nup-adv__dot {
  width: 22px;
  height: 22px;
  margin-top: -11px;
  border-width: 4px;
}

.nup-page.is-pc .nup-adv__item.is-on .nup-adv__dot::after {
  left: 4px;
  top: 0px;
  width: 5px;
  height: 9px;
  border-right-width: 2px;
  border-bottom-width: 2px;
}

.nup-adv-choice {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 160px;
}

.nup-adv-choice__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex-shrink: 0;
}

.nup-adv-choice__head {
  display: flex;
  align-items: center;
}

.nup-adv-choice .nup-adv__dot {
  position: relative;
  left: auto;
  top: auto;
  margin-top: 0;
  margin-right: 18px;
  flex-shrink: 0;
}

.nup-adv-choice__threshold {
  font-size: 30px;
  line-height: 1;
  color: #303030;
  letter-spacing: -0.4px;
  white-space: nowrap;
}

.nup-adv-choice__pick {
  width: 140px;
  height: 48px;
  margin-left: 48px;
  padding: 0;
  border: none;
  border-radius: 24px;
  background: #ff6900;
  color: #fff;
  font-size: 20px;
  font-weight: 600;
  line-height: 48px;
  text-align: center;
  cursor: pointer;
}

.nup-adv__item.is-choice.is-pickable:not(:last-child)::before {
  top: calc(50% - 32px);
  height: calc(50% + 32px + 20px + 80px);
}

.nup-adv-choice__card {
  display: block;
  width: 448px;
  height: auto;
  flex-shrink: 0;
  object-fit: contain;
}

.nup-adv-choice__card.is-picked {
  width: 299px;
  height: 140px;
}

.nup-page.is-h5.is-en .nup-adv-choice__card:not(.is-picked) {
  width: 458px;
}

.nup-page.is-h5.is-en .nup-adv-choice__threshold {
  color: #000;
  font-family: 'BR Hendrix', Arial, sans-serif;
  font-size: 28px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  opacity: 0.8;
}

.nup-page.is-h5.is-en .nup-adv-choice__pick {
  font-family: 'BR Hendrix', Arial, sans-serif;
  font-size: 20px;
}

.nup-page.is-pc .nup-adv-choice {
  min-height: 135px;
}

.nup-page.is-pc .nup-adv-choice .nup-adv__dot {
  margin-top: 0;
  margin-right: 18px;
}

.nup-page.is-pc .nup-adv__item.is-choice.is-pickable:not(:last-child)::before {
  top: calc(50% - 30px);
  height: calc(50% + 30px + 20px + 67px);
}

.nup-page.is-pc .nup-adv-choice__threshold {
  font-size: 20px;
}

.nup-page.is-pc .nup-adv-choice__card {
  width: 649px;
  height: auto;
}

.nup-page.is-pc.is-en .nup-adv-choice__card:not(.is-picked) {
  width: 652px;
}

.nup-page.is-pc .nup-adv-choice__card.is-picked {
  width: 302px;
  height: 135px;
}

.nup-page.is-pc .nup-adv-choice__meta {
  gap: 12px;
}

.nup-page.is-pc .nup-adv-choice__pick {
  margin-left: 40px;
  font-size: 16px;
}
</style>
